import type { Call } from '@twilio/voice-sdk';
import { Sentry } from 'lib-shared-sentry';
import type { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';

import type { AppState } from 'bootstrap.store';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { notificationActions } from '../../../../components/notifications/actions/notification';
import * as telephonyActions from '../../../../components/telephony/actions/telephony';
import { twilioActions } from '../actions/twilioActions';

import { handleDeviceError } from './errorCodes';
import type { CallResult } from './runtime';
import { TwilioRuntime } from './runtime';
import type { SagaSettlement, TwilioEventNotifiers } from './types';

const { telephonyRenewToken } = telephonyActions.actions;

const {
  twilioMakeCall,
  twilioMakeCallFailed,
  twilioMakeInternalCall,
  twilioMakeInternalCallFailed,
  twilioMakeCallback,
  twilioMakeCallbackFailed,
  twilioInit,
  twilioSendDigits,
  twilioClose,
  twilioConnectCall,
  twilioConnectCallFailed,
  twilioConnectCallSucceeded,
  twilioDisconnectCall,
  twilioDisconnectCallSucceeded,
  twilioDisconnectCallFailed,
  twilioFeedback,
  twilioFeedbackSucceeded,
  twilioFeedbackFailed,
  twilioChangeDeviceStatus,
  twilioAddActiveCall,
  twilioEstablishedCall,
  twilioMissedCall,
  twilioDisconnectedCall,
  twilioMakeCallSucceeded,
  twilioMakeInternalCallSucceeded,
  twilioMakeCallbackSucceeded,
} = twilioActions;

const { notificationAdd } = notificationActions;

function buildEventHandlers(
  dispatch: Dispatch<AnyAction>,
): TwilioEventNotifiers {
  return {
    // ── Device ───────────────────────────────────────────────────────────────
    onDeviceStatusChanged: (device) =>
      dispatch(twilioChangeDeviceStatus(device)),
    onDeviceError: (error) => handleDeviceError({ error, dispatch }),

    // ── Incoming / missed ────────────────────────────────────────────────────
    onIncomingCall: ({ from, to }) =>
      dispatch(twilioAddActiveCall(null, from, to, 'inbound', Date.now())),
    onCallMissed: () => dispatch(twilioMissedCall()),

    // ── Call lifecycle ────────────────────────────────────────────────────────
    onCallEstablished: ({ csid, muted }) =>
      dispatch(twilioEstablishedCall({ csid, muted })),
    onCallTerminated: ({ message }) =>
      dispatch(twilioDisconnectedCall({ message })),

    // ── Feedback ─────────────────────────────────────────────────────────────
    onFeedbackSucceeded: (result) => dispatch(twilioFeedbackSucceeded(result)),
    onFeedbackFailed: (error) => dispatch(twilioFeedbackFailed(error)),
  };
}

/**
 * Voice SDK implementation of the Twilio middleware.
 *
 * Owns all Redux concerns: maps action types to TwilioRuntime commands and maps
 * TwilioRuntime promise results back to Redux action dispatches. TwilioRuntime
 * itself has no knowledge of Redux.
 */
export const voiceSdkTwilioMiddleware =
  (): Middleware<unknown, AppState> =>
  (store: MiddlewareAPI<Dispatch<AnyAction>, AppState>) => {
    const getToken = bindActionToPromise(
      store.dispatch,
      telephonyRenewToken,
    ) as () => Promise<{
      data: { token: string; ice_servers: Array<RTCIceServer> | null };
    }>;

    const dispatch = store.dispatch;
    const handlers = buildEventHandlers(dispatch);
    const runtime = new TwilioRuntime(getToken, handlers);

    const makeSettlementCallbacksForAction = (
      action: AnyAction,
    ): SagaSettlement => ({
      resolve: action.meta.resolve,
      reject: action.meta.reject,
    });

    const notifyInternalIssue = (message: string): void => {
      dispatch(
        notificationAdd({
          title: 'Telephony issue',
          text: message,
          className: 'danger',
          icon: 'announcement-slash',
        }),
      );
      Sentry.captureMessage(message, 'error');
    };

    const rejectMissingCsid = ({
      action,
      context,
      onRejected,
    }: {
      action: AnyAction;
      context: string;
      onRejected?: () => void;
    }): void => {
      const message = `Twilio: ${context} was not given any csid in payload params.`;
      console.error(message);
      notifyInternalIssue(message);
      onRejected?.();
      makeSettlementCallbacksForAction(action).reject(message);
    };

    const isTwilioAction = (action: AnyAction) =>
      Boolean(action.type && action.type.indexOf('TWILIO') > -1);

    const routeActionToTwilioRuntime = (action: AnyAction): void => {
      switch (action.type) {
        case twilioInit.toString():
          runtime.initializeDevice().catch(Sentry.captureException);
          break;

        case twilioMakeCall.toString(): {
          const { userId, fromNumber, toNumber } = action.payload;
          const actionCallbacks = makeSettlementCallbacksForAction(action);

          dispatch(
            twilioAddActiveCall(
              userId,
              fromNumber,
              toNumber,
              'outbound',
              Date.now(),
            ),
          );

          runtime
            .makeCall({
              userId: userId as string,
              fromNumber: fromNumber as string,
              toNumber: toNumber as string,
            })
            .then((result: CallResult) => {
              dispatch(
                twilioMakeCallSucceeded({
                  status: result.status,
                  direction: result.direction,
                  userId,
                  from: fromNumber,
                }),
              );

              actionCallbacks.resolve(result);
            })
            .catch((error) => {
              dispatch(twilioMakeCallFailed());
              actionCallbacks.reject(error);
              Sentry.captureException(error);
            });

          break;
        }

        case twilioMakeInternalCall.toString(): {
          const { userId } = action.payload;
          const actionCallbacks = makeSettlementCallbacksForAction(action);
          dispatch(twilioAddActiveCall(userId, 'internal', Date.now()));

          runtime
            .makeInternalCall(userId as string)
            .then((result: CallResult) => {
              dispatch(
                twilioMakeInternalCallSucceeded({
                  status: result.status,
                  direction: result.direction,
                  userId,
                }),
              );

              actionCallbacks.resolve(result);
            })
            .catch((error) => {
              dispatch(twilioMakeInternalCallFailed());
              actionCallbacks.reject(error);
              Sentry.captureException(error);
            });

          break;
        }

        case twilioMakeCallback.toString(): {
          const { csid } = action.payload;

          if (csid == null) {
            rejectMissingCsid({
              action,
              context: 'MakeCallback',
              onRejected: () => dispatch(twilioMakeCallbackFailed()),
            });

            break;
          }

          const actionCallbacks = makeSettlementCallbacksForAction(action);
          dispatch(twilioAddActiveCall(csid, 'callback', Date.now()));

          runtime
            .makeCallback(csid as string | number)
            .then((result: CallResult) => {
              dispatch(
                twilioMakeCallbackSucceeded({
                  status: result.status,
                  csid: result.csid,
                }),
              );

              actionCallbacks.resolve(result);
            })
            .catch((error) => {
              dispatch(twilioMakeCallbackFailed());
              actionCallbacks.reject(error);
              Sentry.captureException(error);
            });

          break;
        }

        case twilioConnectCall.toString(): {
          const connectCsid = action.payload.csid;

          if (connectCsid == null) {
            rejectMissingCsid({
              action,
              context: 'ConnectCall',
              onRejected: () => dispatch(twilioConnectCallFailed()),
            });

            break;
          }

          const actionCallbacks = makeSettlementCallbacksForAction(action);

          runtime
            .connectCall({
              csid: connectCsid as string | number,
              muted: action.payload.muted as boolean | undefined,
              isOnlyListening: action.payload.isOnlyListening as
                | boolean
                | undefined,
            })
            .then((result: CallResult) => {
              dispatch(
                twilioConnectCallSucceeded({
                  status: result.status,
                  direction: result.direction,
                  csid: result.csid,
                }),
              );

              actionCallbacks.resolve(result);
            })
            .catch((error) => {
              dispatch(twilioConnectCallFailed());
              actionCallbacks.reject(error);
              Sentry.captureException(error);
            });

          break;
        }

        case twilioDisconnectCall.toString(): {
          const disconnectCsid = action.payload.csid;

          if (disconnectCsid == null) {
            rejectMissingCsid({ action, context: 'DisconnectCall' });
            break;
          }

          const actionCallbacks = makeSettlementCallbacksForAction(action);

          runtime
            .disconnectCall()
            .then((result) => {
              dispatch(
                twilioDisconnectCallSucceeded({ status: result.status }),
              );

              actionCallbacks.resolve(result);
            })
            .catch((error) => {
              dispatch(twilioDisconnectCallFailed());
              actionCallbacks.reject(error);
              Sentry.captureException(error);
            });
          break;
        }

        case twilioSendDigits.toString():
          runtime.sendDigits(action.payload.digit as string);
          break;

        case twilioFeedback.toString(): {
          const actionCallbacks = makeSettlementCallbacksForAction(action);

          runtime
            .sendFeedback({
              score: action.payload.selectedStar as Call.FeedbackScore,
              issue: action.payload.specificIssue as Call.FeedbackIssue | null,
            })
            .then((result) => {
              handlers.onFeedbackSucceeded(result);
              actionCallbacks.resolve(result);
            })
            .catch((error) => {
              handlers.onFeedbackFailed(error);
              actionCallbacks.reject(error);
            });
          break;
        }

        case twilioClose.toString():
          runtime.shutdown();
          break;
      }
    };

    return (next: Dispatch<AnyAction>) =>
      (action: AnyAction): AnyAction => {
        if (isTwilioAction(action)) {
          try {
            routeActionToTwilioRuntime(action);
          } catch (exception) {
            Sentry.captureException(exception);
          }
        }

        return next(action);
      };
  };
